import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/mocks/categories';
import { MAIN_WEBSITE_URL } from '@/mocks/site';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Route-aware: on the homepage the hero is dark so use light text until scrolled.
  // On other pages there is no dark hero, so always use a solid light bar with dark text.
  const solid = !isHome || scrolled || menuOpen;
  const textClass = solid ? 'text-foreground-900' : 'text-background-50';
  const hoverClass = solid ? 'hover:bg-background-200' : 'hover:bg-background-50/10';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-links-line text-xl" aria-hidden="true"></i>
            </span>
            <span className={`font-heading font-extrabold text-lg tracking-tight whitespace-nowrap ${textClass}`}>
              USFans<span className="text-primary-500">Link</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap transition-colors ${hoverClass} ${textClass}`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/usfans-spreadsheet-guide"
              className="ml-1 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-base" aria-hidden="true"></i>
              </span>
              {t('nav.guide')}
            </Link>
            {isHome ? (
              <a
                href="#categories"
                className={`px-4 py-2 text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap transition-colors ${hoverClass} ${textClass}`}
              >
                {t('nav.categories')}
              </a>
            ) : (
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap transition-colors ${hoverClass} ${textClass}`}
              >
                {t('nav.categories')}
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher light={!solid} />
            <a
              href={MAIN_WEBSITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="ml-1 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {t('nav.visitMain')}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
              </span>
            </a>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher light={!solid} />
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className={`w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${textClass}`}
              aria-label="Menu"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-home-4-line text-lg" aria-hidden="true"></i>
              </span>
              {t('nav.home')}
            </Link>
            <Link
              to="/usfans-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                <i className="ri-shopping-bag-3-line text-lg" aria-hidden="true"></i>
              </span>
              {t('nav.guide')}
            </Link>

            <div className="mt-3 px-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                {t('nav.categories')}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-2.5 py-2 text-xs font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-up-line text-sm" aria-hidden="true"></i>
                    </span>
                    <span className="truncate">{t(`category.${cat.key}`)}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href={MAIN_WEBSITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {t('nav.visitMain')}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
              </span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}